﻿@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginecardview

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.service.mitBerechneterPlatzBelegung
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginecardview.layouts.REGAL_PLATZ_TYP
import org.example.project.enginecardview.layouts.RegalCenterText
import org.example.project.enginecardview.layouts.RegalEbeneRow
import org.example.project.enginecardview.layouts.RegalSettingsPopup
import org.example.project.enginecardview.layouts.RegalStyles
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginetabular.TableStyles
import org.example.project.settings.AppDropdown
import org.example.project.utils.All
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.example.project.service.InfrastrukturApi
import org.example.project.service.GlobalManager
import org.example.project.service.UserSettingsApi
import org.example.project.service.YardApi
import org.example.project.screens_cus.infraAktiveFelder
import org.example.project.screens_cus.infraCodeFieldOrder
import org.example.project.screens_cus.infraPlatzTypOptionen

private val DEFAULT_REGAL_CODE_FIELDS = setOf("regal")
private val UEBERSICHT_FILTER_WIDE = 220.px
private val UEBERSICHT_FILTER_NORMAL = 150.px
private val UEBERSICHT_ZOOM_WIDTH = 190.px

@Composable
fun CUSRegaluebersicht(art: String = org.example.project.service.currentSaveArt()) {
    CUSPlatzKartenUebersicht(art = art, festerPlatzTyp = REGAL_PLATZ_TYP)
}

@Composable
private fun CUSPlatzKartenUebersicht(
    art: String = org.example.project.service.currentSaveArt(),
    festerPlatzTyp: String
) {
    Style(RegalStyles)
    Style(TableStyles)

    val scope = rememberCoroutineScope()
    val refreshVersion = maxOf(
        LocalScreenRefreshVersion.current,
        GlobalManager.jsonDomainVersion("yard"),
        GlobalManager.jsonDomainVersion("yard-kontrollen")
    )

    var plaetze by remember { mutableStateOf<List<All>>(emptyList()) }
    var waStandorte by remember { mutableStateOf<List<All>>(emptyList()) }
    var infra by remember { mutableStateOf<All?>(null) }
    var standortStamm by remember { mutableStateOf<List<All>>(emptyList()) }
    var showRegalSettings by remember { mutableStateOf(false) }
    var regalCodeFields by remember { mutableStateOf(DEFAULT_REGAL_CODE_FIELDS) }
    var regalCodeDraftFields by remember { mutableStateOf(DEFAULT_REGAL_CODE_FIELDS) }
    var datenGeladen by remember { mutableStateOf(false) }

    var selectedPlatzTyp by remember { mutableStateOf<String?>(null) }
    var selectedStandort by remember { mutableStateOf<String?>(null) }
    var selectedHalle by remember { mutableStateOf<String?>(null) }
    var selectedRegal by remember { mutableStateOf<String?>(null) }
    var selectedFach by remember { mutableStateOf<String?>(null) }
    var selectedEbene by remember { mutableStateOf<String?>(null) }
    var selectedTor by remember { mutableStateOf<String?>(null) }
    var selectedBereich by remember { mutableStateOf<String?>(null) }
    var zoom by remember { mutableStateOf(1.0) }

    LaunchedEffect(Unit) {
        val loadedFields = parseRegalCodeFields(UserSettingsApi.loadCurrentUserRegalSettingsFile())
        regalCodeFields = loadedFields
        regalCodeDraftFields = loadedFields
    }

    suspend fun loadGefiltertePlaetze() {
        if (selectedStandort.isNullOrBlank()) {
            plaetze = emptyList()
            waStandorte = emptyList()
            datenGeladen = true
            return
        }
        val selectedPlatzTypTyp = infraPlatzTypOptionen(infra)
            .firstOrNull { it.key == selectedPlatzTyp }
            ?.typ
            ?.takeIf { it.equals(festerPlatzTyp, ignoreCase = true) }
            ?: festerPlatzTyp
            .orEmpty()
        val allePlaetze = InfrastrukturApi.loadPlaetzeFiltered(
            All(
                typ = selectedPlatzTypTyp,
                standort = selectedStandort.orEmpty(),
                halle = selectedHalle.orEmpty(),
                regal = selectedRegal.orEmpty(),
                fach = selectedFach.orEmpty(),
                ebene = selectedEbene.orEmpty(),
                tor = selectedTor?.toIntOrNull(),
                bereich = selectedBereich.orEmpty()
            )
        )
        plaetze = allePlaetze.map { platz ->
            platz.mitBerechneterPlatzBelegung()
        }
        val platzcodes = allePlaetze.mapNotNull { it.platzcode.takeIf(String::isNotBlank) }.distinct()
        waStandorte = YardApi.loadWaStandorteFiltered(
            All(
                standort = selectedStandort.orEmpty(),
                tor = selectedTor?.toIntOrNull(),
                columns = platzcodes
            )
        )
        datenGeladen = true
    }

    LaunchedEffect(art, refreshVersion) {
        infra = InfrastrukturApi.loadInfra()
        standortStamm = KarteiApi.loadStandorte().sortedBy { it.id.toIntOrNull() ?: 0 }
        if (datenGeladen && !selectedStandort.isNullOrBlank()) {
            loadGefiltertePlaetze()
        }
    }

    val standorte = remember(standortStamm) {
        standortStamm.mapNotNull { it.id.takeIf(String::isNotBlank) }.distinct().sortedWith(compareBy<String> { it.toIntOrNull() ?: Int.MAX_VALUE }.thenBy { it })
    }
    val platztypen = remember(infra) {
        infraPlatzTypOptionen(infra).filter { it.typ.equals(festerPlatzTyp, ignoreCase = true) }
    }
    val selectedPlatzTypLabel = platztypen.firstOrNull { it.key == selectedPlatzTyp }?.label.orEmpty()
    val selectedPlatzTypTyp = platztypen.firstOrNull { it.key == selectedPlatzTyp }?.typ.orEmpty()
    val fixedPlatzTypKey = platztypen.firstOrNull()?.key.orEmpty()
    val effectivePlatzTyp = selectedPlatzTyp ?: fixedPlatzTypKey
    val aktiveFelder = infraAktiveFelder(infra, effectivePlatzTyp).toSet()

    fun platzPasstZumTyp(platz: All): Boolean {
        val typ = selectedPlatzTypTyp.ifBlank { festerPlatzTyp }.trim()
        return platz.typ.trim().equals(typ, ignoreCase = true)
    }

    fun feldWert(platz: All, feld: String): String =
        when (feld) {
            "standort" -> platz.standort
            "halle" -> platz.halle
            "regal" -> platz.regal
            "fach" -> platz.fach
            "ebene" -> platz.ebene
            "tor" -> platz.tor?.toString().orEmpty()
            "bereich" -> platz.bereich
            else -> ""
        }

    fun passtZuAktivenFiltern(platz: All, feldAusnahme: String = ""): Boolean =
        platzPasstZumTyp(platz) &&
            (feldAusnahme == "standort" || selectedStandort == null || platz.standort == selectedStandort) &&
            (feldAusnahme == "halle" || selectedHalle == null || platz.halle == selectedHalle) &&
            (feldAusnahme == "regal" || selectedRegal == null || platz.regal == selectedRegal) &&
            (feldAusnahme == "fach" || selectedFach == null || platz.fach == selectedFach) &&
            (feldAusnahme == "ebene" || selectedEbene == null || platz.ebene == selectedEbene) &&
            (feldAusnahme == "tor" || selectedTor == null ||
                platz.tor?.toString().orEmpty().padStart(3, '0') == selectedTor ||
                platz.tor?.toString().orEmpty() == selectedTor) &&
            (feldAusnahme == "bereich" || selectedBereich == null || platz.bereich == selectedBereich)

    val selectedStandortData = standortStamm.firstOrNull { it.id == selectedStandort }
    fun range(von: String, bis: String, pad: Int): List<String> {
        val start = von.toIntOrNull() ?: return emptyList()
        val end = bis.toIntOrNull() ?: start
        return (start..end).map { it.toString().padStart(pad, '0') }
    }
    val hallen = remember(selectedStandortData) { range(selectedStandortData?.halleVon.orEmpty(), selectedStandortData?.halleBis.orEmpty(), 2) }
    val regale = remember(selectedStandortData) { range(selectedStandortData?.regalVon.orEmpty(), selectedStandortData?.regalBis.orEmpty(), 2) }
    val faecher = remember(selectedStandortData) { range(selectedStandortData?.fachVon.orEmpty(), selectedStandortData?.fachBis.orEmpty(), 3) }
    val ebenen = remember(selectedStandortData) { range(selectedStandortData?.ebeneVon.orEmpty(), selectedStandortData?.ebeneBis.orEmpty(), 2) }
    val tore = remember(selectedStandortData) { range(selectedStandortData?.toreVon.orEmpty(), selectedStandortData?.toreBis.orEmpty(), 3) }
    val bereiche = remember(plaetze) { plaetze.mapNotNull { it.bereich.takeIf(String::isNotBlank) }.distinct().sorted() }

    val aktivePlaetze = plaetze.filter {
        passtZuAktivenFiltern(it)
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            width(100.percent)
            height(100.percent)
        }
    }) {
        Div({
            style {
                flexShrink(0)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                flexWrap(FlexWrap.Wrap)
                gap(10.px)
                padding(12.px, 16.px)
            }
        }) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    alignItems(AlignItems.Center)
                    flexWrap(FlexWrap.Wrap)
                    gap(10.px)
                }
            }) {
                Dropdown("Standort", standorte) {
                    selectedStandort = it
                    selectedPlatzTyp = null
                    selectedHalle = null
                    selectedRegal = null
                    selectedFach = null
                    selectedEbene = null
                    selectedTor = null
                    selectedBereich = null
                }
                AllDropdown("Platztyp", platztypen) {
                    selectedPlatzTyp = it ?: fixedPlatzTypKey.takeIf { key -> key.isNotBlank() }
                    selectedHalle = null
                    selectedRegal = null
                    selectedFach = null
                    selectedEbene = null
                    selectedTor = null
                    selectedBereich = null
                }
                if ("halle" in aktiveFelder) {
                    Dropdown("Halle", hallen) {
                        selectedHalle = it
                        selectedRegal = null
                        selectedFach = null
                        selectedEbene = null
                        selectedTor = null
                        selectedBereich = null
                    }
                }
                if ("regal" in aktiveFelder) {
                    Dropdown("Regal", regale) {
                        selectedRegal = it
                        selectedFach = null
                        selectedEbene = null
                    }
                }
                if ("fach" in aktiveFelder) {
                    Dropdown("Fach", faecher) {
                        selectedFach = it
                        selectedEbene = null
                    }
                }
                if ("ebene" in aktiveFelder) {
                    Dropdown("Ebene", ebenen) { selectedEbene = it }
                }
                if ("tor" in aktiveFelder) {
                    Dropdown("Tor", tore) { selectedTor = it }
                }
                if ("bereich" in aktiveFelder) {
                    Dropdown("Bereich", bereiche) { selectedBereich = it }
                }
                CardViewActionButton("Suchen", "images/suche.svg") {
                    scope.launch { loadGefiltertePlaetze() }
                }
                CardViewActionButton("Einstellungen", "images/settings.svg") {
                    regalCodeDraftFields = regalCodeFields
                    showRegalSettings = !showRegalSettings
                }
                CardViewActionButton("Reset", "images/refresh.svg") {
                    selectedPlatzTyp = null; selectedStandort = null; selectedHalle = null; selectedRegal = null; selectedFach = null; selectedEbene = null; selectedTor = null; selectedBereich = null
                    plaetze = emptyList(); waStandorte = emptyList(); datenGeladen = false
                }

                Div({
                    style {
                        width(UEBERSICHT_ZOOM_WIDTH)
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(8.px)
                        color(DashboardTheme.TEXT_LIGHT)
                        fontWeight("700")
                    }
                }) {
                    Text("Zoom: ${(zoom * 100).toInt()}%")
                    Input(type = InputType.Range) {
                        attr("min", "0.5")
                        attr("max", "2.0")
                        attr("step", "0.1")
                        value(zoom.toString())
                        onInput { e ->
                            e.target.value.toDoubleOrNull()?.let { zoom = it }
                        }
                    }
                }
            }
        }

        if (showRegalSettings) {
            RegalSettingsPopup(
                fields = regalCodeDraftFields,
                availableFields = aktiveFelder,
                onToggle = { field, checked ->
                    regalCodeDraftFields = if (checked) regalCodeDraftFields + field else regalCodeDraftFields - field
                },
                onClose = { showRegalSettings = false },
                onSave = {
                    val next = regalCodeDraftFields
                    regalCodeFields = next
                    showRegalSettings = false
                    scope.launch {
                        UserSettingsApi.saveCurrentUserRegalSettingsFile(regalCodeFieldsJson(next))
                    }
                }
            )
        }

        when {
            selectedStandort == null -> RegalCenterText("Bitte Standort auswählen und Laden drücken.")
            platztypen.isEmpty() -> RegalCenterText("Platztyp $festerPlatzTyp ist in der Infrastruktur nicht eingerichtet.")
            !datenGeladen -> RegalCenterText("Filter setzen und Laden drücken.")
            aktivePlaetze.isEmpty() -> RegalCenterText("Keine passenden Plätze gefunden.")
            else -> {
                val plaetzeNachEbene = aktivePlaetze
                    .groupBy { if ("ebene" in aktiveFelder) it.ebene else "" }
                    .toList()
                    .sortedByDescending { (k, _) -> k.toIntOrNull() ?: 0 }

                Div({
                    style {
                        flex(1)
                        overflowX("auto")
                        overflowY("auto")
                        padding(12.px)
                    }
                }) {
                    Div({
                        style {
                            property("transform", "scale($zoom)")
                            property("transform-origin", "top left")
                            property("transition", "transform 0.2s ease-in-out")
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(12.px)
                            width(100.percent)
                            property("min-width", "max-content")
                        }
                    }) {
                        plaetzeNachEbene.forEach { (_, list) ->
                            RegalEbeneRow(list, waStandorte, infra, effectivePlatzTyp, regalCodeFields)
                        }
                    }
                }
            }
        }
    }
}

private fun parseRegalCodeFields(raw: String): Set<String> {
    if (raw.isBlank()) return DEFAULT_REGAL_CODE_FIELDS
    return runCatching {
        val root = Json.parseToJsonElement(raw).jsonObject
        infraCodeFieldOrder()
            .filter { root[it]?.jsonPrimitive?.booleanOrNull == true }
            .toSet()
    }.getOrDefault(DEFAULT_REGAL_CODE_FIELDS)
}

private fun regalCodeFieldsJson(fields: Set<String>): String =
    buildJsonObject {
        infraCodeFieldOrder().forEach { field ->
            put(field, field in fields)
        }
    }.toString()

@Composable
private fun AllDropdown(label: String, options: List<All>, onChange: (String?) -> Unit) {
    var selectedValue by remember(label, options) { mutableStateOf("") }

    Div({ style { width(if (label == "Standort" || label == "Platztyp") UEBERSICHT_FILTER_WIDE else UEBERSICHT_FILTER_NORMAL) } }) {
        AppDropdown(
            options = buildList {
                add(All(key = "", label = label))
                addAll(options)
            },
            selectedValue = selectedValue,
            onChange = {
                selectedValue = it
                onChange(it.ifBlank { null })
            },
            attrs = {
                classes(TableStyles.button)
            }
        )
    }
    CardViewDivider()
}

@Composable
private fun Dropdown(label: String, options: List<String>, onChange: (String?) -> Unit) {
    var selectedValue by remember(label, options) { mutableStateOf("") }

    Div({ style { width(if (label == "Standort") UEBERSICHT_FILTER_WIDE else UEBERSICHT_FILTER_NORMAL) } }) {
        AppDropdown(
            options = buildList {
                add(All(key = "", label = label))
                addAll(options.map { All(key = it, label = it) })
            },
            selectedValue = selectedValue,
            onChange = {
                selectedValue = it
                onChange(it.ifBlank { null })
            },
            attrs = {
                classes(TableStyles.button)
            }
        )
    }
    CardViewDivider()
}

